import { useState, useEffect } from 'react';
import axios from 'axios';
import TransactionForm from './TransactionForm';
import ImportCSV from './ImportCSV';
import TransactionChart from './TransactionChart';
import Login from './Login';
import Register from './Register';
import Investments from './Investments';
import ExpenseBreakdown from './ExpenseBreakdown';


const API = '/api';

axios.interceptors.request.use(config => {
  const t = localStorage.getItem('token');
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});


const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,300&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #f7f6f3;
    --surface: #ffffff;
    --border: #e8e6e1;
    --text: #1a1917;
    --muted: #9b9690;
    --accent: #880015;
    --expense: #dc2626;
    --income: #16a34a;
    --transfer: #7c3aed;
    --radius: 6px;
  }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    font-size: 14px;
    line-height: 1.5;
  }

  .app {
    max-width: 1100px;
    margin: 0 auto;
    padding: 40px 24px;
  }

  /* Header */
  .header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--border);
  }

  .logo {
    font-family: 'DM Mono', monospace;
    font-size: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }

  .logo span { color: var(--accent); }

  .header-meta {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: var(--muted);
    letter-spacing: 0.5px;
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 2px;
    margin-bottom: 28px;
    background: var(--border);
    padding: 2px;
    border-radius: var(--radius);
    width: fit-content;
  }

  .tab {
    padding: 6px 16px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: var(--muted);
    border-radius: 4px;
    transition: all 0.15s;
  }

  .tab.active {
    background: var(--surface);
    color: var(--text);
    font-weight: 500;
  }

  /* Summary Cards */
  .summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 28px;
  }

  .card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 20px;
  }

  .card-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 8px;
  }

  .card-value {
    font-family: 'DM Mono', monospace;
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -1px;
  }

  .card-value.expense { color: var(--expense); }
  .card-value.income { color: var(--income); }

  /* Table */
  .table-wrap {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
  }

  .table-title {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--muted);
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--muted);
    font-weight: 400;
    text-align: left;
    padding: 10px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--bg);
  }

  td {
    padding: 12px 20px;
    border-bottom: 1px solid var(--border);
    font-size: 13px;
  }

  tr:last-child td { border-bottom: none; }
  tr:hover td { background: var(--bg); }

  .td-mono {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
  }

  .td-date {
    color: var(--muted);
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    white-space: nowrap;
  }

  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.3px;
  }

  .badge-expense { background: #fee2e2; color: var(--expense); }
  .badge-income { background: #dcfce7; color: var(--income); }
  .badge-transfer { background: #ede9fe; color: var(--transfer); }

  .amount-expense { color: var(--expense); font-family: 'DM Mono', monospace; }
  .amount-income { color: var(--income); font-family: 'DM Mono', monospace; }

  /* Action buttons */
  .btn-row { display: flex; gap: 6px; }

  .btn {
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid var(--border);
    background: transparent;
    cursor: pointer;
    font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    color: var(--muted);
    transition: all 0.15s;
  }

  .btn:hover { color: var(--text); border-color: #ccc; }
  .btn-edit.active { color: var(--accent); border-color: var(--accent); }
  .btn-delete:hover { color: var(--expense); border-color: var(--expense); }

  /* Form */
  .form-wrap {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 24px;
  }

  .form-title {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 20px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .form-group label {
    font-size: 11px;
    font-family: 'DM Mono', monospace;
    color: var(--muted);
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .form-group input,
  .form-group select {
    padding: 8px 10px;
    border: 1px solid var(--border);
    border-radius: 4px;
    background: var(--bg);
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: var(--text);
    outline: none;
    transition: border-color 0.15s;
  }

  .form-group input:focus,
  .form-group select:focus {
    border-color: var(--accent);
    background: white;
  }

  .form-actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }

  .btn-primary {
    padding: 8px 20px;
    background: var(--text);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    transition: opacity 0.15s;
  }

  .btn-primary:hover { opacity: 0.85; }

  .btn-cancel {
    padding: 8px 16px;
    background: transparent;
    color: var(--muted);
    border: 1px solid var(--border);
    border-radius: 4px;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
  }

  /* Import */
  .import-wrap {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 24px;
  }

  .import-zone {
    border: 1.5px dashed var(--border);
    border-radius: var(--radius);
    padding: 32px;
    text-align: center;
    margin-bottom: 16px;
    transition: border-color 0.15s;
  }

  .import-zone:hover { border-color: var(--accent); }

  .import-zone p {
    color: var(--muted);
    font-size: 13px;
    margin-top: 8px;
  }

  .import-filename {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: var(--accent);
    margin-top: 8px;
  }

  .status-msg {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    color: var(--income);
    margin-top: 8px;
  }

  .empty-state {
    text-align: center;
    padding: 48px 20px;
    color: var(--muted);
    font-family: 'DM Mono', monospace;
    font-size: 12px;
  }
`;

function App() {
  
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [authPage, setAuthPage] = useState('login');
  const [transactions, setTransactions] = useState([]);
  const [editData, setEditData] = useState(null);
  const [tab, setTab] = useState('transactions');
  const [showTransactions, setShowTransactions] = useState(false);
  const [page, setPage] = useState('transactions');
  
  const [filters, setFilters] = useState({ date: '', description: '', type: '', category: '', amount: '', linkedTransaction: '' });
  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));

  const fetchTransactions = () => {
    axios.get(`${API}/transactions`).then(res => setTransactions(res.data));
  };

  useEffect(() => { 
    if (token) fetchTransactions(); 
  }, [token]);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/transactions/${id}`);
    fetchTransactions();
  };

  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalTransfers = transactions
    .filter(t => t.type === 'transfer' && !['Stock', 'Savings', 'Self-Transfer'].includes(t.category))
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const net = totalIncome + totalExpenses + totalTransfers;
  const netExpenses = totalExpenses + totalTransfers < 0 ? totalExpenses + totalTransfers : totalExpenses;

  const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
  const filtered = transactions.filter(t => {
    const date = new Date(t.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }).toLowerCase();
    return (
      date.includes(filters.date.toLowerCase()) &&
      (t.description || '').toLowerCase().includes(filters.description.toLowerCase()) &&
      (t.type || '').toLowerCase().includes(filters.type.toLowerCase()) &&
      (t.category || '').toLowerCase().includes(filters.category.toLowerCase()) &&
      (t.linkedTransactionId || '').toString().includes(filters.linkedTransaction) &&
      String(t.amount).includes(filters.amount)
    );
  });

  if (!token) {
    return authPage === 'login'
      ? <Login onLogin={setToken} onGoRegister={() => setAuthPage('register')} />
      : <Register onLogin={setToken} onGoLogin={() => setAuthPage('login')} />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">

        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <div className="logo">fin<span>trak</span></div>
            <div style={{ display: 'flex', gap: '2px', background: 'var(--border)', padding: '2px', borderRadius: 'var(--radius)' }}>
              <button
                className={`tab ${page === 'transactions' ? 'active' : ''}`}
                onClick={() => setPage('transactions')}
              >Transactions</button>
              <button
                className={`tab ${page === 'investments' ? 'active' : ''}`}
                onClick={() => setPage('investments')}
              >Investments</button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {page === 'transactions' &&
              <div className="header-meta">{transactions.length} transactions</div>
            }
            <button className="btn" onClick={() => { localStorage.removeItem('token'); setToken(null); setTransactions([]); }}>logout</button>
          </div>
        </div>
        {page === 'transactions' ? (
          <>
            <div className="summary">
              <div className="card">
                <div className="card-label">Income</div>
                <div className="card-value income">{fmt(totalIncome)}</div>
              </div>
              <div className="card">
                <div className="card-label">Expenses</div>
                <div className="card-value expense">{fmt(netExpenses)}</div>
              </div>
              <div className="card">
                <div className="card-label">Transfers</div>
                <div className={`card-value ${totalTransfers >= 0 ? 'income' : 'expense'}`}>{fmt(totalTransfers)}</div>
              </div>          
              <div className="card">
                <div className="card-label">Net</div>
                <div className={`card-value ${net >= 0 ? 'income' : 'expense'}`}>{fmt(net)}</div>
              </div>
            </div>

            <div className="tabs">
              <button className={`tab ${tab === 'transactions' ? 'active' : ''}`} onClick={() => setTab('transactions')}>Transactions</button>
              <button className={`tab ${tab === 'add' ? 'active' : ''}`} onClick={() => { setTab('add'); setEditData(null); }}>Add</button>
              <button className={`tab ${tab === 'import' ? 'active' : ''}`} onClick={() => setTab('import')}>Import CSV</button>
            </div>

            {tab === 'add' && (
              <div className="form-wrap">
                <div className="form-title">{editData ? 'Edit Transaction' : 'New Transaction'}</div>
                <TransactionForm
                  editData={editData}
                  onSaved={() => { fetchTransactions(); setEditData(null); setTab('transactions'); }}
                  transactions={transactions}
                  onCancel={() => { setEditData(null); setTab('transactions'); }}
                />
              </div>
            )}

            {tab === 'import' && (
              <ImportCSV onImported={() => { fetchTransactions(); setTab('transactions'); }} />
            )}

            {tab === 'transactions' && (
              <>
                <TransactionChart transactions={transactions} />
                <ExpenseBreakdown transactions={transactions} />
                <div className="table-wrap">
                  <div className="table-header">
                    <div className="table-title">All Transactions</div>
                    <button
                      className="btn"
                      onClick={() => setShowTransactions(prev => !prev)}
                    >
                      {showTransactions ? '▲ hide' : '▼ show'}
                    </button>
                  </div>
                  {showTransactions && (filtered.length === 0 ? (
                    <div className="empty-state">{transactions.length === 0 ? 'no transactions yet' : 'no results'}</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Category</th>
                          <th>Amount</th>
                          <th>Linked Transaction</th>
                          <th></th>
                        </tr>
                        <tr>
                          {['date', 'description', 'type', 'category', 'amount', 'linkedTransaction'].map(key => (
                            <th key={key} style={{ padding: '6px 20px' }}>
                              <input
                                value={filters[key]}
                                onChange={e => updateFilter(key, e.target.value)}
                                placeholder={`filter...`}
                                style={{
                                  width: '100%',
                                  padding: '4px 6px',
                                  border: '1px solid var(--border)',
                                  borderRadius: '3px',
                                  background: 'var(--surface)',
                                  fontFamily: 'DM Mono, monospace',
                                  fontSize: '11px',
                                  color: 'var(--text)',
                                  outline: 'none'
                                }}
                              />
                            </th>
                          ))}
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(t => (
                          <tr key={t.id}>
                            <td className="td-date">{new Date(t.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                            <td>{t.description}</td>
                            <td><span className={`badge badge-${t.type}`}>{t.type}</span></td>
                            <td className="td-mono" style={{ color: 'var(--muted)', fontSize: '12px' }}>{t.category || '—'}</td>
                            <td className={`amount-${t.type}`} style={{ color: Number(t.amount) < 0 ? 'var(--expense)' : 'var(--income)', fontFamily: 'DM Mono, monospace' }}>
                              {Number(t.amount) < 0 ? '−' : '+'}{fmt(Math.abs(Number(t.amount)))}
                            </td>
                            <td>
                              {t.linkedTransactionId && (() => {
                                const linked = transactions.find(x => x.id === t.linkedTransactionId);
                                return linked ? (
                                  <span style={{
                                    fontFamily: 'DM Mono, monospace',
                                    fontSize: '11px',
                                    color: 'var(--transfer)',
                                    background: '#ede9fe',
                                    padding: '2px 8px',
                                    borderRadius: '20px',
                                    display: 'inline-block',
                                  }}>
                                    ⇄ {linked.description} (${linked.amount})
                                  </span>
                                ) : null;
                              })()}
                            </td>
                            <td>
                              <div className="btn-row">
                                <button
                                  className={`btn btn-edit ${editData?.id === t.id ? 'active' : ''}`}
                                  onClick={() => { setEditData(t); setTab('add'); }}
                                >edit</button>
                                <button className="btn btn-delete" onClick={() => handleDelete(t.id)}>del</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="4" style={{ padding: '12px 20px', fontFamily: 'DM Mono, monospace', fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--muted)', borderTop: '2px solid var(--border)' }}>
                            {filtered.length} transactions
                          </td>
                          <td style={{ padding: '12px 20px', fontFamily: 'DM Mono, monospace', fontWeight: '500', borderTop: '2px solid var(--border)', color: (() => {
                            const total = filtered.reduce((sum, t) => sum + Number(t.amount), 0);
                            return total < 0 ? 'var(--expense)' : 'var(--income)';
                          })() }}>
                            {fmt(filtered.reduce((sum, t) => sum + Number(t.amount), 0))}
                          </td>
                          <td colSpan="2" style={{ borderTop: '2px solid var(--border)' }}></td>
                        </tr>
                      </tfoot>
                    </table>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <Investments />
        )}
      </div>
    </>
  );
}

export default App;